// BlockRenderer.tsx — MJOS-028, DEM-26 (RTL)
//
// The dashboard's self-owned content composition layer. Replaces @measured/puck.
//
// Doctrine (locked):
//   • Page content lives in `page_content.content` as a flat object:
//       { blocks: [ { type, props, children? }, ... ] }
//   • The TYPE is a string key into a `BlockConfig.components` map
//     (e.g. "Hero", "SectionHeader", "Deck", "CoverSlide").
//   • The PROPS are the React props for that component, verbatim.
//   • CHILDREN are nested blocks — used by slot/wrapper components
//     (e.g. <Deck> → slides). Children are passed to the parent component
//     as its React `children` prop, recursively rendered by BlockRenderer.
//
// This file is intentionally tiny. The complexity of the doctrine lives in
// the per-domain block libraries (ArchitectureBlocks.tsx, PitchDeckBlocks.tsx)
// — not in the renderer.
//
// DEM-26: optional `dir` prop. When passed, the renderer wraps its output
// in a `<div dir={dir}>`. Radix UI primitives + CSS logical properties (used
// in ArchitectureBlocks) auto-flip on the ambient `dir`. Default is no
// wrapper at all — preserves the prior render shape for every existing call
// site that doesn't opt in.

import type { ComponentType } from "react";

// ── Schema types ───────────────────────────────────────────────────────────
export type Block = {
  type: string;
  props: Record<string, unknown>;
  children?: Block[];
};

export type PageBlocks = { blocks: Block[] };

export type BlockConfig = {
  components: Record<string, ComponentType<Record<string, unknown> & { children?: React.ReactNode }>>;
};

// ── Unknown-type warning ───────────────────────────────────────────────────
// An unregistered block type used to render as NOTHING, silently: no error, no
// gap, no console output. On 2026-08-15 that had hidden 47 blocks across 11
// pages — 18 of them the warning callouts on tailormind-core, the page every
// agent reads first. A green write, a green build, and nothing on the page.
//
// The failure was never the missing component; it was that nothing said so.
// Warn ONCE per unknown type (not per render, and not per block) so the signal
// survives a busy page without becoming noise of its own.
const _warnedUnknownTypes = new Set<string>();

function warnUnknownBlockType(type: string) {
  if (_warnedUnknownTypes.has(type)) return;
  _warnedUnknownTypes.add(type);
  console.warn(
    `[BlockRenderer] Unknown block type "${type}" — rendering nothing. ` +
      `The block exists in page_content but is not in this config's components map, ` +
      `so its content is invisible on the page. Register it, or fix the type in the row.`,
  );
}

// ── Renderer ───────────────────────────────────────────────────────────────
export function BlockRenderer({
  config,
  blocks,
  dir,
}: {
  config: BlockConfig;
  blocks: Block[];
  dir?: "ltr" | "rtl";
}) {
  const rendered = (
    <>
      {blocks.map((b, i) => {
        const Component = config.components[b.type];
        if (!Component) {
          warnUnknownBlockType(b.type);
          return null;
        }
        const childrenEl = b.children?.length ? (
          <BlockRenderer config={config} blocks={b.children} />
        ) : undefined;
        const key = (b.props?.id as string | undefined) ?? `${b.type}-${i}`;
        return (
          <Component key={key} {...b.props}>
            {childrenEl}
          </Component>
        );
      })}
    </>
  );

  if (!dir) return rendered;
  return <div dir={dir}>{rendered}</div>;
}
